import { z } from "zod";

/**
 * AI Model Sync History Schema
 * @package @genieartinc/schemas
 */

export const AiModelSyncHistorySchema = z.object({
  id: z.string(),
  syncId: z.string(), // "sync-1761027722658"
  provider: z.string(), // "fal"

  // Summary Stats
  totalModels: z.number(),
  addedCount: z.number().default(0),
  updatedCount: z.number().default(0),
  deprecatedCount: z.number().default(0),
  removedCount: z.number().default(0),
  unchangedCount: z.number().default(0),
  errorCount: z.number().default(0),

  // Detailed Changes
  changes: z.record(z.string(), z.unknown()).default({}),
  errors: z.array(z.unknown()).default([]),
  warnings: z.array(z.unknown()).default([]),

  // Performance Metrics
  duration: z.number(), // milliseconds
  cacheHitRate: z.number().optional(), // 0.988 (98.8%)
  llmCallsCount: z.number().optional(),

  // Family/Purpose Coverage
  familyCoverage: z.number().optional(), // 0.796 (79.6%)
  purposeCoverage: z.number().optional(), // 0.988 (98.8%)

  // Timestamps
  startedAt: z.date(),
  completedAt: z.date(),
  createdAt: z.date(),

  // Relations
  modelId: z.string().optional(),
});

export type AiModelSyncHistory = z.infer<typeof AiModelSyncHistorySchema>;

// Create DTO
export const CreateAiModelSyncHistorySchema = AiModelSyncHistorySchema.omit({
  id: true,
  createdAt: true,
});

export type CreateAiModelSyncHistory = z.infer<typeof CreateAiModelSyncHistorySchema>;

// Sync Summary DTO (for reports)
export const AiSyncSummarySchema = z.object({
  syncId: z.string(),
  provider: z.string(),
  totalModels: z.number(),
  addedCount: z.number(),
  updatedCount: z.number(),
  deprecatedCount: z.number(),
  removedCount: z.number(),
  unchangedCount: z.number(),
  errorCount: z.number(),
  duration: z.number(),
  cacheHitRate: z.number().optional(),
  familyCoverage: z.number().optional(),
  purposeCoverage: z.number().optional(),
  startedAt: z.date(),
  completedAt: z.date(),
});

export type AiSyncSummary = z.infer<typeof AiSyncSummarySchema>;

// Sync Status (for live updates)
export const AiSyncStatusSchema = z.object({
  isRunning: z.boolean(),
  currentSyncId: z.string().optional(),
  progress: z.number().optional(), // 0-100
  lastSync: AiSyncSummarySchema.optional(),
});

export type AiSyncStatus = z.infer<typeof AiSyncStatusSchema>;
